import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSermonSeries, useUpsertSeries } from '../hooks/useSermonSeries';
import { useDevotionalSeries } from '../hooks/useDevotionalSeries';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const FormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.5rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  max-width: 600px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Textarea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const Button = styled.button`
  background-color: var(--primary);
  color: #fff;
  padding: 0.6rem 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
  }
`;

export const SermonSeriesFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();

  const { data: seriesData } = useSermonSeries();
  const { data: devotionalSeries } = useDevotionalSeries();
  const existing = seriesData?.find((s) => s.id === id);

  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [devotionalSeriesId, setDevotionalSeriesId] = useState('');

  useEffect(() => {
    if (existing) {
      setTitle(existing.title ?? '');
      setSubtitle(existing.subtitle ?? '');
      setDescription(existing.description ?? '');
      setImageUrl(existing.image_url ?? '');
      setDevotionalSeriesId(existing.devotional_series_id ?? '');
    }
  }, [existing]);

  const upsert = useUpsertSeries();

  const handleUpload = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const { error } = await supabase.storage
      .from('post-images')
      .upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    setImageUrl(data.publicUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await upsert.mutateAsync({
      id,
      title,
      subtitle,
      description,
      image_url: imageUrl || undefined,
      devotional_series_id: devotionalSeriesId || undefined,
    });
    navigate('/sermon-series');
  };

  return (
    <Page>
      <h1 style={{ marginBottom: '1rem' }}>{editing ? 'Edit' : 'New'} Sermon Series</h1>
      <FormCard>
        <Form onSubmit={handleSubmit}>
        <label>
          Title
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label>
          Subtitle
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </label>
        <label>
          Description
          <Textarea value={description} rows={4} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label>
          Linked Devotional Series
          <Select value={devotionalSeriesId} onChange={(e) => setDevotionalSeriesId(e.target.value)}>
            <option value="">None</option>
            {(devotionalSeries ?? []).map((series) => (
              <option key={series.id} value={series.id}>
                {series.title}
              </option>
            ))}
          </Select>
        </label>
        <img
          src={imageUrl}
          alt={imageUrl ? 'Sermon series image preview' : ''}
          style={{ width: '100%', objectFit: 'cover', borderRadius: 6, marginBottom: 8 }}
        />
        {/* <label>
          Image URL
          <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
        </label> */}
        <label>
          Or Upload Image
          <Input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              if (e.target.files?.[0]) {
                try {
                  await handleUpload(e.target.files[0]);
                } catch (err) {
                  // eslint-disable-next-line no-alert
                  alert('Upload failed');
                }
              }
            }}
          />
        </label>
        <Button type="submit" disabled={upsert.isPending}>
          {upsert.isPending ? 'Saving…' : 'Save'}
        </Button>
        </Form>
      </FormCard>
    </Page>
  );
};

export default SermonSeriesFormPage;
